import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Table, Nav, Tab, Form, Alert, Spinner, Modal } from 'react-bootstrap';
import { FiArrowLeft, FiCheckCircle, FiGift, FiCalendar, FiMapPin, FiCloud, FiEdit } from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.css';
import { getTripClaimById, patchTripClaimStatus } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import PolicyInfoCard from '../../components/Common/PolicyInfoCard';
import ClientInfoSections from '../../components/Common/ClientInfoSections';
import WeatherTriggerParamsSection from '../../components/Common/WeatherTriggerParamsSection';
import './AdminTripsCheckClaimDetail.css';
import { resolvePrecipTrigger } from '../../utils/precipTrigger';

const TRIP_CLAIM_STATUS_OPTIONS = [
    'Pre-validated',
    'Not pre-validated',
    'Validated',
    'Expired',
    'Paid',
    'Not validated',
    'Documents received',
    'No answer yet',
];

const Label = ({ children }) => (
    <div style={{ 
        fontSize: '0.875rem', 
        color: '#6b7280', 
        fontWeight: 500,
        marginBottom: '0.5rem',
        lineHeight: '1.4'
    }}>
        {children}
    </div>
);

const Value = ({ children }) => (
    <div style={{ 
        fontWeight: 600, 
        color: '#1f2937',
        fontSize: '0.95rem',
        minHeight: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        lineHeight: '1.5'
    }}>
        {children}
    </div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const TriggerItem = ({ label, children, full = false }) => (
    <div className={`cc-trigger-item${full ? ' cc-trigger-item--full' : ''}`}>
        <div className="cc-trigger-label">{label}</div>
        <div className="cc-trigger-value">{children}</div>
    </div>
);

const AdminTripClaimDetail = () => {
    const navigate = useNavigate();
    const { claimId } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [claim, setClaim] = useState(location.state?.claim || null);
    const policyId = location.state?.policyId;
    const [copiedForecast, setCopiedForecast] = useState(false);
    const [activeTab, setActiveTab] = useState('detail');
    const [selectedClaimStatus, setSelectedClaimStatus] = useState('');
    const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
    const [statusUpdateError, setStatusUpdateError] = useState(null);
    const [statusUpdateResult, setStatusUpdateResult] = useState(null);
    const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);

    const fetchClaim = useCallback(async () => {
        if (!claimId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await getTripClaimById(claimId);
            setClaim(data?.claim || null);
        } catch (e) {
            setError(handleApiError(e, 'Erreur lors du chargement du sinistre'));
        } finally {
            setLoading(false);
        }
    }, [claimId]);

    useEffect(() => {
        fetchClaim();
    }, [fetchClaim]);

    useEffect(() => {
        if (claim?.claim_status) {
            setSelectedClaimStatus(claim.claim_status);
        }
    }, [claim?.claim_status]);

    const handleOpenStatusConfirmModal = (e) => {
        e.preventDefault();
        if (!selectedClaimStatus) {
            setStatusUpdateError('Veuillez sélectionner un statut de sinistre');
            return;
        }
        if (selectedClaimStatus === claim?.claim_status) {
            setStatusUpdateError('Le nouveau statut est identique au statut actuel');
            return;
        }
        setStatusUpdateError(null);
        setShowStatusConfirmModal(true);
    };

    const handleCloseStatusConfirmModal = () => {
        if (statusUpdateLoading) return;
        setShowStatusConfirmModal(false);
    };

    const confirmUpdateTripClaimStatus = async () => {
        if (!selectedClaimStatus) return;

        try {
            setStatusUpdateLoading(true);
            setStatusUpdateError(null);
            setStatusUpdateResult(null);
            const data = await patchTripClaimStatus(claimId, { claim_status: selectedClaimStatus });
            setStatusUpdateResult(data);
            setShowStatusConfirmModal(false);
            await fetchClaim();
        } catch (err) {
            setStatusUpdateError(handleApiError(err, 'Erreur lors de la mise à jour du statut du sinistre'));
        } finally {
            setStatusUpdateLoading(false);
        }
    };

    const mapObservationDataToWeatherSections = (observationRows = []) => {
        const rows = Array.isArray(observationRows) ? observationRows : [];
        const weatherData = [];
        const weatherTemp = [];
        const weatherWind = [];

        rows.forEach((row) => {
            const rawTime = row?.time != null ? String(row.time).trim() : '';
            const match = rawTime.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})/);
            const forecast_year = match ? match[1] : null;
            const forecast_month = match ? match[2] : null;
            const forecast_day = match ? match[3] : null;
            const forecast_hour = match ? `${String(match[4]).padStart(2, '0')}:${match[5]}` : (rawTime || null);

            const base = {
                forecast_day,
                forecast_month,
                forecast_year,
                forecast_hour,
            };

            if (row?.prcp != null && row.prcp !== '') {
                weatherData.push({ ...base, value: row.prcp });
            }
            if (row?.temperature != null && row.temperature !== '') {
                weatherTemp.push({ ...base, value: row.temperature });
            }
            if (row?.wind != null && row.wind !== '') {
                weatherWind.push({ ...base, value: row.wind });
            }
        });

        return { weatherData, weatherTemp, weatherWind };
    };

    const copyForecastDataToClipboard = async () => {
        if (!claim?.forecast_info?.weather_data || claim.forecast_info.weather_data.length === 0) return;

        const headers = ['Date', 'Heure', 'Précipitation (mm)'];
        const rows = claim.forecast_info.weather_data.map(data => {
            const date = data.forecast_day && data.forecast_month && data.forecast_year
                ? `${data.forecast_day}/${data.forecast_month}/${data.forecast_year}`
                : '';
            return [
                date,
                data.forecast_hour || '',
                data.value || '0,0'
            ];
        });

        const csvContent = [
            headers.join('\t'),
            ...rows.map(row => row.join('\t'))
        ].join('\n');

        try {
            await navigator.clipboard.writeText(csvContent);
            setCopiedForecast(true);
            setTimeout(() => setCopiedForecast(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = csvContent;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedForecast(true);
                setTimeout(() => setCopiedForecast(false), 2000);
            } catch (fallbackErr) {
                console.error('Erreur lors de la copie:', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du sinistre</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Chargement du sinistre...</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du sinistre</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-danger">{error}</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!claim) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du sinistre</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de sinistre disponible pour l'identifiant: {claimId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const get = (obj, path, fallback = '—') => {
        try {
            const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return v ?? fallback;
        } catch {
            return fallback;
        }
    };

    const formatWeatherParameter = (value) => {
        if (value == null || value === '') return 'N/A';
        if (Array.isArray(value)) return value.filter(Boolean).join(', ') || 'N/A';
        return String(value);
    };
    const subTripsClaim = claim?.reference_info?.subcoverage_trips;
    const firstSubClaim = Array.isArray(subTripsClaim) && subTripsClaim.length > 0
        ? subTripsClaim[0]
        : (Array.isArray(claim?.reference_info?.SubCoverage) && claim.reference_info.SubCoverage[0])
            || (claim?.reference_info?.SubCoverage && typeof claim.reference_info.SubCoverage === 'object' ? claim.reference_info.SubCoverage : {})
            || claim?.subcoverage_trip
            || {};

    const precip = resolvePrecipTrigger(
        [claim?.CheckClaims?.prcp_is_cumulated, claim?.prcp_is_cumulated, claim?.reference_info?.prcp_is_cumulated],
        [claim?.CheckClaims, firstSubClaim]
    );
    const displayOrNa = (value) => (value == null || value === '' ? 'N/A' : value);

    return (
        <div className="dashboard-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">Détail du sinistre</h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'detail')}>
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="detail">Informations</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="update-status">
                            <FiEdit className="me-2" />
                            Mettre à jour le statut
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey="detail">

            <Section title="Informations principales">
                <Row className="mb-2">
                    <Col md={4}><Label>Code sinistre</Label><Value>{get(claim, ['claim_code'])}</Value></Col>
                    <Col md={4}><Label>Statut</Label><Value><FiCheckCircle className="me-1" />{get(claim, ['claim_status'])}</Value></Col>
                    <Col md={4}><Label>Étape</Label><Value>{get(claim, ['claim_stage'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Date du sinistre</Label><Value><FiCalendar className="me-1" />{get(claim, ['date_of_claim'])}</Value></Col>
                    <Col md={4}><Label>Date de paiement</Label><Value><FiCalendar className="me-1" />{get(claim, ['payment_date'])}</Value></Col>
                    <Col md={4}><Label>Montant</Label><Value><FaEuroSign className="me-1" />{get(claim, ['amount_of_claim'], '0')}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Montant annulation</Label><Value><FaEuroSign className="me-1 text-warning" />{get(claim, ['amount_of_claim_cancellation'])}</Value></Col>
                    <Col md={4}><Label>Activité maintenue</Label><Value>{get(claim, ['activity_maintained']) === 'true' || get(claim, ['activity_maintained']) === true ? 'Oui' : 'Non'}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Base déclenchement</Label><Value>{get(claim, ['trigger_based_on'])}</Value></Col>
                    <Col md={4}><Label>Ouvert/Fermé</Label><Value><Badge bg={get(claim, ['open_closed_claims']) === 'open' ? 'success' : 'secondary'}>{get(claim, ['open_closed_claims']) === 'open' ? 'OPEN' : 'CLOSED'}</Badge></Value></Col>
                    <Col md={4}><Label>IBAN reçu</Label><Value><Badge bg={get(claim, ['is_iban_received']) ? 'success' : 'secondary'}>{get(claim, ['is_iban_received']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['claim_last_modified_at'])}</Value></Col>
                    <Col md={4}><Label>Modifié par</Label><Value>{get(claim, ['claim_last_modified_by'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Cash (maintien)</Label><Value><FaEuroSign className="me-1 text-success" />{get(claim, ['cash_claim_amount_maintain'], '0')}</Value></Col>
                    <Col md={4}><Label>Voucher (maintien)</Label><Value><FiGift className="me-1 text-primary" />{get(claim, ['voucher_claim_amount_maintain'], '0')}</Value></Col>
                    <Col md={4}><Label>Cash (annulation)</Label><Value><FaEuroSign className="me-1 text-warning" />{get(claim, ['cash_claim_amount_cancellation'], '0')}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Voucher (annulation)</Label><Value><FiGift className="me-1 text-info" />{get(claim, ['voucher_claim_amount_cancellation'], '0')}</Value></Col>
                    <Col md={4}><Label>Nouveau montant</Label><Value><FaEuroSign className="me-1" />{get(claim, ['new_amount_of_claim'], '0')}</Value></Col>
                    <Col md={4}><Label>Nouveau montant annulation</Label><Value><FaEuroSign className="me-1 text-warning" />{get(claim, ['new_amount_of_claim_cancellation'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Nouveau Cash (maintien)</Label><Value><FaEuroSign className="me-1 text-success" />{get(claim, ['new_cash_claim_amount_maintain'], '0')}</Value></Col>
                    <Col md={4}><Label>Nouveau Voucher (maintien)</Label><Value><FiGift className="me-1 text-primary" />{get(claim, ['new_voucher_claim_amount_maintain'], '0')}</Value></Col>
                    <Col md={4}><Label>Nouveau Cash (annulation)</Label><Value><FaEuroSign className="me-1 text-warning" />{get(claim, ['new_cash_claim_amount_cancellation'], '0')}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Confirmé</Label><Value><Badge bg={get(claim, ['is_confirmed']) ? 'success' : 'secondary'}>{get(claim, ['is_confirmed']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Date de choix</Label><Value>{get(claim, ['choice_date'])}</Value></Col>
                    <Col md={4}><Label>Date confirmation choix</Label><Value>{get(claim, ['choice_confirmation_date'])}</Value></Col>
                    <Col md={4}><Label>Date signature choix</Label><Value>{get(claim, ['choice_signature_date'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Mode de paiement</Label><Value>{get(claim, ['payment_mode'])}</Value></Col>
                    <Col md={4}><Label>Choix signé</Label><Value>{get(claim, ['activity_choice_signed'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Délai dépassé</Label><Value><Badge bg={get(claim, ['deadline_past']) ? 'danger' : 'success'}>{get(claim, ['deadline_past']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                    <Col md={4}><Label>Email prévision envoyé</Label><Value><Badge bg={get(claim, ['is_forecast_email_send']) ? 'success' : 'secondary'}>{get(claim, ['is_forecast_email_send']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                    <Col md={4}><Label>Date envoi email prévision</Label><Value>{get(claim, ['forecast_email_sent_at'])}</Value></Col>
                </Row>
            </Section>

            <ClientInfoSections
                clientInfo={claim?.client_info}
                partnerInfo={claim?.partner_info}
                submissionDateFallback={claim?.policy_info?.date_rib_submission}
            />

            {claim?.CheckClaims && (() => {
                const checkClaim = claim.CheckClaims;
                return (
                    <Section title="Vérification du sinistre">
                        <WeatherTriggerParamsSection
                            triggerType={
                                firstSubClaim?.trigger_type
                                ?? checkClaim?.trigger_type
                                ?? claim?.reference_info?.trigger_type
                            }
                            weatherParameter={formatWeatherParameter(
                                firstSubClaim?.weather_parameter
                                ?? claim?.reference_info?.weather_parameter
                            )}
                            lastModifiedBy={get(checkClaim, ['check_claim_last_modified_by'], null)}
                            lastModifiedAt={get(checkClaim, ['check_claim_last_modified_at'], null)}
                            precipMode={precip.mode}
                            precipThreshold={precip.lowThreshold}
                            precipUpperThreshold={precip.upperThreshold}
                            precipUnit={precip.unit}
                            precipHours={precip.hours}
                            temperatureThreshold={get(checkClaim, ['temperature_trigger_hour_inf'], get(firstSubClaim, ['temperature_trigger_hour_inf'], null))}
                            temperatureHours={get(checkClaim, ['temperature_trigger_number_hours'], get(firstSubClaim, ['temperature_trigger_number_hours'], null))}
                            windThreshold={get(checkClaim, ['wind_trigger_hour_inf'], get(firstSubClaim, ['wind_trigger_hour_inf'], null))}
                            windHours={get(checkClaim, ['wind_trigger_number_hours'], get(firstSubClaim, ['wind_trigger_number_hours'], null))}
                        />

                        {(() => {
                            const weatherParams = Array.isArray(firstSubClaim?.weather_parameter)
                                ? firstSubClaim.weather_parameter
                                : (firstSubClaim?.weather_parameter ? [firstSubClaim.weather_parameter] : []);
                            const paramsNorm = weatherParams.map((p) => String(p).toLowerCase());
                            const hasPrecip = paramsNorm.length === 0
                                || paramsNorm.some((p) => p.includes('precip'));
                            const hasTemp = paramsNorm.length === 0
                                || paramsNorm.some((p) => p.includes('temp'));
                            const hasWind = paramsNorm.length === 0
                                || paramsNorm.some((p) => p.includes('wind') || p.includes('vent'));

                            const tempTriggered = checkClaim?.temperature_is_triggered_forecast;
                            const precipTriggered = checkClaim?.precipitation_is_triggered_forecast;
                            const windTriggered = checkClaim?.wind_is_triggered_forecast;
                            const tempHours = checkClaim?.temperature_count_hours_triggered;
                            const precipHours = checkClaim?.precipitation_count_hours_triggered;
                            const windHours = checkClaim?.wind_count_hours_triggered;

                            const tempTriggeredObs = checkClaim?.temperature_is_triggered_observation;
                            const precipTriggeredObs = checkClaim?.precipitation_is_triggered_observation;
                            const windTriggeredObs = checkClaim?.wind_is_triggered_observation;
                            const tempHoursObs = checkClaim?.temperature_count_hours_triggered_observation;
                            const precipHoursObs = checkClaim?.precipitation_count_hours_triggered_observation;
                            const windHoursObs = checkClaim?.wind_count_hours_triggered_observation;
                            const countHoursObs = checkClaim?.count_hours_triggered_observation;

                            const boolBadge = (value) => {
                                if (value === null || value === undefined) {
                                    return <Badge bg="warning">En attente de traitement</Badge>;
                                }
                                return (
                                    <Badge bg={value === true ? 'success' : 'secondary'}>
                                        {value === true ? 'Oui' : 'Non'}
                                    </Badge>
                                );
                            };

                            const statusOrNa = (applicable, value) => (
                                !applicable
                                    ? <Badge bg="secondary">Non applicable</Badge>
                                    : boolBadge(value)
                            );

                            const hoursValue = (applicable, value) => {
                                if (!applicable) return '—';
                                if (value === null || value === undefined) return '—';
                                return value;
                            };

                            return (
                                <div className="cc-trigger-panels">
                                    <section className="cc-trigger-panel" aria-label="Forecast">
                                        <h3 className="cc-trigger-panel__title">Forecast</h3>

                                        <div className="cc-trigger-grid">
                                            <TriggerItem label={<><FiCheckCircle className="me-1" aria-hidden />Forecast déclenché</>} full>
                                                {boolBadge(checkClaim?.is_triggered_forecast)}
                                            </TriggerItem>
                                        </div>

                                        <div className="cc-trigger-grid">
                                            <TriggerItem label="Température déclenchée">
                                                {statusOrNa(hasTemp, tempTriggered)}
                                            </TriggerItem>
                                            <TriggerItem label="Nb heures température">
                                                {hoursValue(hasTemp, tempHours)}
                                            </TriggerItem>
                                        </div>

                                        <div className="cc-trigger-grid">
                                            <TriggerItem label="Précipitation déclenchée">
                                                {statusOrNa(hasPrecip, precipTriggered)}
                                            </TriggerItem>
                                            <TriggerItem label="Nb heures précipitation">
                                                {hoursValue(hasPrecip, precipHours)}
                                            </TriggerItem>
                                        </div>

                                        <div className="cc-trigger-grid">
                                            <TriggerItem label="Vent déclenché">
                                                {statusOrNa(hasWind, windTriggered)}
                                            </TriggerItem>
                                            <TriggerItem label="Nb heures vent">
                                                {hoursValue(hasWind, windHours)}
                                            </TriggerItem>
                                        </div>
                                    </section>

                                    <section className="cc-trigger-panel" aria-label="Observation">
                                        <h3 className="cc-trigger-panel__title">Observation</h3>

                                        <div className="cc-trigger-grid">
                                            <TriggerItem label="Observation déclenchée">
                                                {boolBadge(checkClaim?.is_triggered_observation)}
                                            </TriggerItem>
                                            <TriggerItem label="Nb heures déclenchées">
                                                {countHoursObs === null || countHoursObs === undefined
                                                    ? '—'
                                                    : countHoursObs}
                                            </TriggerItem>
                                        </div>

                                        <div className="cc-trigger-grid">
                                            <TriggerItem label="Température déclenchée">
                                                {statusOrNa(hasTemp, tempTriggeredObs)}
                                            </TriggerItem>
                                            <TriggerItem label="Nb heures température">
                                                {hoursValue(hasTemp, tempHoursObs)}
                                            </TriggerItem>
                                        </div>

                                        <div className="cc-trigger-grid">
                                            <TriggerItem label="Précipitation déclenchée">
                                                {statusOrNa(hasPrecip, precipTriggeredObs)}
                                            </TriggerItem>
                                            <TriggerItem label="Nb heures précipitation">
                                                {hoursValue(hasPrecip, precipHoursObs)}
                                            </TriggerItem>
                                        </div>

                                        <div className="cc-trigger-grid">
                                            <TriggerItem label="Vent déclenché">
                                                {statusOrNa(hasWind, windTriggeredObs)}
                                            </TriggerItem>
                                            <TriggerItem label="Nb heures vent">
                                                {hoursValue(hasWind, windHoursObs)}
                                            </TriggerItem>
                                        </div>
                                    </section>
                                </div>
                            );
                        })()}
                    </Section>
                );
            })()}

            <PolicyInfoCard
                companyName={claim?.partner_info?.company_name}
                policyCode={claim?.policy_info?.policy_code}
                reservationNumber={claim?.activity_info?.reservation_number}
                stayStartDate={claim?.activity_info?.activity_start_date}
                stayEndDate={claim?.activity_info?.activity_end_date}
                newStayStartDate={claim?.activity_info?.new_trip_start_date}
                newStayEndDate={claim?.activity_info?.new_trip_end_date}
                effectiveStartDate={claim?.activity_info?.trip_effective_start_date}
                effectiveEndDate={claim?.activity_info?.trip_effective_end_date}
                newEffectiveStartDate={claim?.activity_info?.new_trip_effective_start_date}
                newEffectiveEndDate={claim?.activity_info?.new_trip_effective_end_date}
                referenceName={claim?.reference_info?.reference_name}
                address={
                    claim?.activity_info?.combined_address
                    || [
                        claim?.activity_info?.activity_address,
                        claim?.activity_info?.activity_zip_code,
                        claim?.activity_info?.activity_city,
                        claim?.activity_info?.activity_country,
                    ].filter(Boolean).join(', ')
                    || claim?.forecast_info?.address
                }
                latitude={
                    claim?.activity_info?.latitude
                    ?? claim?.latitude
                    ?? claim?.forecast_info?.latitude
                }
                longitude={
                    claim?.activity_info?.longitude
                    ?? claim?.longitude
                    ?? claim?.forecast_info?.longitude
                }
                dailySumInsuredMaintain={claim?.policy_info?.daily_sum_insured_maintain}
                dailySumInsuredCancellation={claim?.policy_info?.daily_sum_insured_cancellation}
                totalSumInsuredMaintain={claim?.policy_info?.total_sum_insured_maintain}
                totalSumInsuredCancellation={claim?.policy_info?.total_sum_insured_cancellation}
            />

            {/* Informations prévision — aligné trips/check-claims */}
            {claim?.forecast_info && claim.forecast_info.forecast_id && (
                <Section title="Informations prévision">
                    <Row className="mb-2">
                        <Col md={4}>
                            <Label>Source de données</Label>
                            <Value>{get(claim, ['forecast_info', 'data_source'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label><FiCloud className="me-1" />Total mm précipitation</Label>
                            <Value>{get(claim, ['forecast_info', 'total_mm_prc'])}</Value>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}>
                            <Label>Coordonnées</Label>
                            <Value>{get(claim, ['forecast_info', 'latitude'])}, {get(claim, ['forecast_info', 'longitude'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label>Jour du trip</Label>
                            <Value>{get(claim, ['forecast_info', 'trip_day'], 'N/A')}</Value>
                        </Col>
                        <Col md={4}>
                            <Label>Date init</Label>
                            <Value>
                                {get(claim, ['forecast_info', 'meteomatics_init_day'])}/
                                {get(claim, ['forecast_info', 'meteomatics_init_month'])}/
                                {get(claim, ['forecast_info', 'meteomatics_init_year'])}
                            </Value>
                        </Col>
                        <Col md={4}>
                            <Label>Heure exécution (UTC)</Label>
                            <Value>{get(claim, ['forecast_info', 'meteomatics_run_hour_utc'])}</Value>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}>
                            <Label>Adresse</Label>
                            <Value>{get(claim, ['forecast_info', 'address'], 'N/A')}</Value>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <Label>Dernière modification</Label>
                            <Value>{get(claim, ['forecast_info', 'forecast_last_modified_at'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label>Modifié par</Label>
                            <Value>{get(claim, ['forecast_info', 'forecast_last_modified_by'])}</Value>
                        </Col>
                    </Row>
                    <WeatherDataSections
                        weatherData={claim.forecast_info.weather_data}
                        weatherTemp={claim.forecast_info.weather_temp}
                        weatherWind={claim.forecast_info.weather_wind}
                        enableCopy
                        onCopyPrecipitation={copyForecastDataToClipboard}
                        copied={copiedForecast}
                        tableSize="sm"
                        bordered
                        className="mt-3"
                        idPrefix="trip-claim"
                    />
                </Section>
            )}

            {/* Informations référence */}
            {claim?.reference_info && (
                <Section title="Informations référence">
                    <Row className="mb-2">
                        <Col md={4}><Label>Nom référence</Label><Value>{get(claim, ['reference_info', 'reference_name'])}</Value></Col>
                        <Col md={4}><Label>Paramètre météo</Label><Value>{formatWeatherParameter(firstSubClaim?.weather_parameter ?? claim.reference_info.weather_parameter)}</Value></Col>
                        <Col md={4}>
                            <Label>Déclenchement cumulé</Label>
                            <Value>
                                <Badge bg={precip.isCumulated ? 'success' : 'secondary'}>
                                    {precip.isCumulated ? 'Oui' : 'Non'}
                                </Badge>
                                <span className="ms-2 text-muted small">({precip.modeLabel})</span>
                            </Value>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        {precip.isCumulated ? (
                            <>
                                <Col md={4}><Label>Seuil bas cumulé des précipitations (mm)</Label><Value>{displayOrNa(precip.lowThreshold)}</Value></Col>
                                <Col md={4}><Label>Seuil haut cumulé des précipitations (mm)</Label><Value>{displayOrNa(precip.upperThreshold)}</Value></Col>
                            </>
                        ) : (
                            <>
                                <Col md={4}><Label>Seuil horaire inférieur des précipitations (mm/h)</Label><Value>{displayOrNa(precip.lowThreshold)}</Value></Col>
                                <Col md={4}><Label>Seuil horaire supérieur des précipitations (mm/h)</Label><Value>{displayOrNa(precip.upperThreshold)}</Value></Col>
                                <Col md={4}><Label>Nombre d’heures de déclenchement des précipitations</Label><Value>{displayOrNa(precip.hours)}</Value></Col>
                            </>
                        )}
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Seuil de déclenchement de la température</Label><Value>{get(claim, ['CheckClaims', 'temperature_trigger_hour_inf'], get(firstSubClaim, ['temperature_trigger_hour_inf']))}</Value></Col>
                        <Col md={4}><Label>Nombre d’heures de déclenchement de la température</Label><Value>{get(claim, ['CheckClaims', 'temperature_trigger_number_hours'], get(firstSubClaim, ['temperature_trigger_number_hours']))}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Seuil de déclenchement du vent</Label><Value>{get(claim, ['CheckClaims', 'wind_trigger_hour_inf'], get(firstSubClaim, ['wind_trigger_hour_inf']))}</Value></Col>
                        <Col md={4}><Label>Nombre d’heures de déclenchement du vent</Label><Value>{get(claim, ['CheckClaims', 'wind_trigger_number_hours'], get(firstSubClaim, ['wind_trigger_number_hours']))}</Value></Col>
                    </Row>
                </Section>
            )}

            {/* Dernière observation */}
            {claim?.last_observation_info && claim.last_observation_info.last_observation_id && (
                <Section title="Dernière observation">
                    <Row className="mb-2">
                        <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['last_observation_info', 'last_data_source'])}</Value></Col>
                        <Col md={4}><Label>Station</Label><Value>{get(claim, ['last_observation_info', 'last_station_name'])}</Value></Col>
                        <Col md={4}><Label>ID Station</Label><Value>{get(claim, ['last_observation_info', 'last_station_id'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Jour d'observation</Label><Value>{get(claim, ['last_observation_info', 'trip_day'])}</Value></Col>
                        <Col md={4}><Label>Date d'initialisation</Label><Value>
                            {get(claim, ['last_observation_info', 'last_observation_init_day'])}/
                            {get(claim, ['last_observation_info', 'last_observation_init_month'])}/
                            {get(claim, ['last_observation_info', 'last_observation_init_year'])}
                        </Value></Col>
                        <Col md={4}><Label>Heure d'exécution</Label><Value>{get(claim, ['last_observation_info', 'last_observation_run_hour'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Coordonnées station</Label><Value>
                            {get(claim, ['last_observation_info', 'last_latitude_station'])}, {get(claim, ['last_observation_info', 'last_longitude_station'])}
                        </Value></Col>
                        <Col md={4}><Label>Total mm précipitation</Label><Value>{get(claim, ['last_observation_info', 'last_total_mm_prc'], '0,0')}</Value></Col>
                        <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['last_observation_info', 'last_observation_last_modified_at'])}</Value></Col>
                    </Row>
                    {(() => {
                        const observationRows = claim?.last_observation_info?.last_observation_data;
                        if (!Array.isArray(observationRows) || observationRows.length === 0) {
                            return (
                                <p className="text-muted mb-0 mt-2">Aucune donnée d'observation disponible.</p>
                            );
                        }
                        const { weatherData, weatherTemp, weatherWind } = mapObservationDataToWeatherSections(observationRows);
                        return (
                            <WeatherDataSections
                                weatherData={weatherData}
                                weatherTemp={weatherTemp}
                                weatherWind={weatherWind}
                                titlePrefix="Données d'observation"
                                enableCopy
                                tableSize="sm"
                                bordered
                                className="mt-3"
                                idPrefix="trip-claim-observation"
                            />
                        );
                    })()}
                </Section>
            )}

            {/* Observations Meteomatics */}
            {claim?.meteomatics_observations_info && (
                <Section title="Observations Meteomatics">
                    {claim.meteomatics_observations_info.meteomatics_observation_id ? (
                        <>
                            <Row className="mb-2">
                                <Col md={4}><Label>ID Observation</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_observation_id'])}</Value></Col>
                                <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_data_source'])}</Value></Col>
                                <Col md={4}><Label>Station</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_station_name'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>ID Station</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_station_id'])}</Value></Col>
                                <Col md={4}><Label>Date d'initialisation</Label><Value>
                                    {get(claim, ['meteomatics_observations_info', 'meteomatics_init_day'])}/
                                    {get(claim, ['meteomatics_observations_info', 'meteomatics_init_month'])}/
                                    {get(claim, ['meteomatics_observations_info', 'meteomatics_init_year'])}
                                </Value></Col>
                                <Col md={4}><Label>Heure d'exécution</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_observation_run_hour'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Coordonnées station</Label><Value>
                                    {get(claim, ['meteomatics_observations_info', 'meteomatics_latitude_station'])}, {get(claim, ['meteomatics_observations_info', 'meteomatics_longitude_station'])}
                                </Value></Col>
                                <Col md={4}><Label>Total mm précipitation</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_total_mm_prc'], '0,0')}</Value></Col>
                                <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_observation_last_modified_at'])}</Value></Col>
                            </Row>
                            {claim.meteomatics_observations_info.meteomatics_observation_data && Array.isArray(claim.meteomatics_observations_info.meteomatics_observation_data) && claim.meteomatics_observations_info.meteomatics_observation_data.length > 0 && (
                                <div className="mt-3">
                                    <Label>Données d'observation</Label>
                                    <Table striped bordered hover size="sm" className="mt-2">
                                        <thead>
                                            <tr>
                                                <th>Heure</th>
                                                <th>Précipitation (mm)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {claim.meteomatics_observations_info.meteomatics_observation_data.map((obs, idx) => (
                                                <tr key={idx}>
                                                    <td>{obs.time || 'N/A'}</td>
                                                    <td>{obs.prcp || '0,0'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-muted mb-0">Aucune observation Meteomatics disponible</p>
                    )}
                </Section>
            )}

            {/* Observations Meteofrance */}
            {claim?.meteofrance_observations_info && (
                <Section title="Observations Meteofrance">
                    {claim.meteofrance_observations_info.meteofrance_observation_id ? (
                        <>
                            <Row className="mb-2">
                                <Col md={4}><Label>ID Observation</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_observation_id'])}</Value></Col>
                                <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_data_source'])}</Value></Col>
                                <Col md={4}><Label>Station</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_station_name'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>ID Station</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_station_id'])}</Value></Col>
                                <Col md={4}><Label>Date d'initialisation</Label><Value>
                                    {get(claim, ['meteofrance_observations_info', 'meteofrance_observation_init_day'])}/
                                    {get(claim, ['meteofrance_observations_info', 'meteofrance_observation_init_month'])}/
                                    {get(claim, ['meteofrance_observations_info', 'meteofrance_observation_init_year'])}
                                </Value></Col>
                                <Col md={4}><Label>Heure d'exécution</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_observation_run_hour'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Coordonnées station</Label><Value>
                                    {get(claim, ['meteofrance_observations_info', 'meteofrance_latitude_station'])}, {get(claim, ['meteofrance_observations_info', 'meteofrance_longitude_station'])}
                                </Value></Col>
                                <Col md={4}><Label>Total mm précipitation</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_total_mm_prc'], '0,0')}</Value></Col>
                                <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_observation_last_modified_at'])}</Value></Col>
                            </Row>
                            {claim.meteofrance_observations_info.meteofrance_observation_data && Array.isArray(claim.meteofrance_observations_info.meteofrance_observation_data) && claim.meteofrance_observations_info.meteofrance_observation_data.length > 0 && (
                                <div className="mt-3">
                                    <Label>Données d'observation</Label>
                                    <Table striped bordered hover size="sm" className="mt-2">
                                        <thead>
                                            <tr>
                                                <th>Heure</th>
                                                <th>Précipitation (mm)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {claim.meteofrance_observations_info.meteofrance_observation_data.map((obs, idx) => (
                                                <tr key={idx}>
                                                    <td>{obs.time || 'N/A'}</td>
                                                    <td>{obs.prcp || '0,0'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-muted mb-0">Aucune observation Meteofrance disponible</p>
                    )}
                </Section>
            )}

            {/* Observations Meteostat */}
            {claim?.meteostat_observations_info && (
                <Section title="Observations Meteostat">
                    {claim.meteostat_observations_info.meteostat_observation_id ? (
                        <>
                            <Row className="mb-2">
                                <Col md={4}><Label>ID Observation</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_observation_id'])}</Value></Col>
                                <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_data_source'])}</Value></Col>
                                <Col md={4}><Label>Station</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_station_name'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>ID Station</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_station_id'])}</Value></Col>
                                <Col md={4}><Label>Date d'initialisation</Label><Value>
                                    {get(claim, ['meteostat_observations_info', 'meteostat_observation_init_day'])}/
                                    {get(claim, ['meteostat_observations_info', 'meteostat_observation_init_month'])}/
                                    {get(claim, ['meteostat_observations_info', 'meteostat_observation_init_year'])}
                                </Value></Col>
                                <Col md={4}><Label>Heure d'exécution</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_observation_run_hour'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Coordonnées station</Label><Value>
                                    {get(claim, ['meteostat_observations_info', 'meteostat_latitude_station'])}, {get(claim, ['meteostat_observations_info', 'meteostat_longitude_station'])}
                                </Value></Col>
                                <Col md={4}><Label>Total mm précipitation</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_total_mm_prc'], '0,0')}</Value></Col>
                                <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_observation_last_modified_at'])}</Value></Col>
                            </Row>
                            {claim.meteostat_observations_info.meteostat_observation_data && Array.isArray(claim.meteostat_observations_info.meteostat_observation_data) && claim.meteostat_observations_info.meteostat_observation_data.length > 0 && (
                                <div className="mt-3">
                                    <Label>Données d'observation</Label>
                                    <Table striped bordered hover size="sm" className="mt-2">
                                        <thead>
                                            <tr>
                                                <th>Heure</th>
                                                <th>Précipitation (mm)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {claim.meteostat_observations_info.meteostat_observation_data.map((obs, idx) => (
                                                <tr key={idx}>
                                                    <td>{obs.time || 'N/A'}</td>
                                                    <td>{obs.prcp || '0,0'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-muted mb-0">Aucune observation Meteostat disponible</p>
                    )}
                </Section>
            )}

                    </Tab.Pane>

                    <Tab.Pane eventKey="update-status">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">Mettre à jour le statut du sinistre</h5>
                                <p className="text-muted small">
                                    Endpoint : <code>PATCH /api/trips/claims/{'{claim_id}'}/status</code> — tous partenaires trips.
                                    Aucun e-mail n&apos;est envoyé.
                                </p>

                                {statusUpdateLoading && (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3 mb-0">Mise à jour du statut en cours...</p>
                                    </div>
                                )}

                                {!statusUpdateLoading && statusUpdateError && (
                                    <Alert variant="danger" dismissible onClose={() => setStatusUpdateError(null)}>
                                        <strong>Erreur :</strong> {statusUpdateError}
                                    </Alert>
                                )}

                                {!statusUpdateLoading && statusUpdateResult && (
                                    <Alert variant="success" className="mb-3" dismissible onClose={() => setStatusUpdateResult(null)}>
                                        <strong>Statut mis à jour avec succès.</strong>
                                        {(statusUpdateResult.claim_status || statusUpdateResult.claim_stage) && (
                                            <div className="mt-2">
                                                {statusUpdateResult.claim_status && (
                                                    <div><strong>Statut :</strong> {statusUpdateResult.claim_status}</div>
                                                )}
                                                {statusUpdateResult.claim_stage && (
                                                    <div><strong>Étape :</strong> {statusUpdateResult.claim_stage}</div>
                                                )}
                                            </div>
                                        )}
                                    </Alert>
                                )}

                                {!statusUpdateLoading && (
                                    <Form onSubmit={handleOpenStatusConfirmModal}>
                                        <Form.Group className="mb-3">
                                            <Form.Label><strong>Nouveau statut</strong> <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                value={selectedClaimStatus}
                                                onChange={(e) => setSelectedClaimStatus(e.target.value)}
                                                required
                                            >
                                                <option value="">Sélectionner un statut...</option>
                                                {TRIP_CLAIM_STATUS_OPTIONS.map((status) => (
                                                    <option key={status} value={status}>{status}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        {claim && (
                                            <Alert variant="info">
                                                <strong>Statut actuel :</strong> {claim.claim_status || '—'}
                                                <br />
                                                <strong>Étape actuelle :</strong> {claim.claim_stage || '—'}
                                            </Alert>
                                        )}

                                        <div className="d-flex gap-2">
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                disabled={
                                                    !selectedClaimStatus
                                                    || statusUpdateLoading
                                                    || selectedClaimStatus === claim?.claim_status
                                                }
                                            >
                                                <FiEdit className="me-2" />
                                                Mettre à jour
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                onClick={() => {
                                                    setSelectedClaimStatus(claim?.claim_status || '');
                                                    setStatusUpdateError(null);
                                                    setStatusUpdateResult(null);
                                                }}
                                                disabled={statusUpdateLoading}
                                            >
                                                Réinitialiser
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            <Modal
                show={showStatusConfirmModal}
                onHide={handleCloseStatusConfirmModal}
                centered
                size="lg"
            >
                <Modal.Header closeButton={!statusUpdateLoading}>
                    <Modal.Title>
                        <FiCheckCircle className="me-2" />
                        Confirmer la mise à jour du statut
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-3">
                        <strong>Attention :</strong> cette action mettra à jour le statut du sinistre.
                        Aucun e-mail ne sera envoyé au client.
                    </Alert>

                    <Card className="mb-3">
                        <Card.Header>
                            <strong>Informations police</strong>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-2">
                                <strong>Code police :</strong>{' '}
                                {claim?.policy_info?.policy_code || '—'}
                            </p>
                            <p className="mb-0">
                                <strong>ID sinistre :</strong> {claim?.claim_id || claimId || '—'}
                            </p>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Header>
                            <strong>Informations client</strong>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-2">
                                <strong>Code client :</strong>{' '}
                                {claim?.client_info?.client_code || '—'}
                            </p>
                            <p className="mb-2">
                                <strong>Nom :</strong>{' '}
                                {[claim?.client_info?.first_name, claim?.client_info?.last_name]
                                    .filter(Boolean)
                                    .join(' ') || '—'}
                            </p>
                            <p className="mb-2">
                                <strong>Email :</strong> {claim?.client_info?.email || '—'}
                            </p>
                            <p className="mb-0">
                                <strong>Téléphone :</strong> {claim?.client_info?.phone_number || '—'}
                            </p>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header>
                            <strong>Changement de statut</strong>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-2">
                                <strong>Statut actuel :</strong> {claim?.claim_status || '—'}
                            </p>
                            <p className="mb-2">
                                <strong>Nouveau statut :</strong> {selectedClaimStatus || '—'}
                            </p>
                            <p className="mb-0 text-muted small">
                                Endpoint : <code>PATCH /api/trips/claims/{'{claim_id}'}/status</code>
                            </p>
                        </Card.Body>
                    </Card>

                    {statusUpdateError && (
                        <Alert variant="danger" className="mt-3 mb-0">
                            <strong>Erreur :</strong> {statusUpdateError}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleCloseStatusConfirmModal}
                        disabled={statusUpdateLoading}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="primary"
                        onClick={confirmUpdateTripClaimStatus}
                        disabled={statusUpdateLoading || !selectedClaimStatus}
                    >
                        {statusUpdateLoading ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Mise à jour...
                            </>
                        ) : (
                            <>
                                <FiCheckCircle className="me-2" />
                                Confirmer la mise à jour
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminTripClaimDetail;


